import { useState } from "react";
import { Sparkles } from "lucide-react";
import { useVanDesign } from "./VanDesignContext";
import { VanHeader, NCard, NBtn, SegGroup, Chip, MonoBadge } from "./vanUI";
import { BUDGET_TIERS, GOAL_PRIORITIES, tierOf } from "./vanData";

export default function VanDesignGoals({ onBack }) {
  const { project, setGoals, setScreen } = useVanDesign();
  const g = project.goals;
  const [notes, setNotes] = useState(g.notes || "");

  const togglePriority = (p) => {
    const has = g.priorities.includes(p);
    setGoals({ priorities: has ? g.priorities.filter((x) => x !== p) : [...g.priorities, p] });
  };

  const tier = tierOf(g.budget_tier);
  const scopeFactor = 1 + Math.max(0, g.priorities.length - 3) * 0.12;
  const lo = Math.round((tier.projectRange[0] * scopeFactor) / 100) * 100;
  const hi = Math.round((tier.projectRange[1] * scopeFactor) / 100) * 100;

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-[#FDFBF7] max-w-[412px] mx-auto" data-testid="van-goals">
      <VanHeader title="Goals & Budget" sub="Step 2 of 3 · What matters" onBack={onBack} step="2 / 3" testid="van-goals-header" />
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 py-4 space-y-4">
        <div className="grid grid-cols-3 gap-1.5">
          {[0, 1, 2].map((i) => <div key={i} className={`h-2 rounded-full border-2 border-black ${i <= 1 ? "bg-[#D4AF37]" : "bg-white"}`} />)}
        </div>

        <div className="space-y-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/60 mb-1.5">Who travels</p>
            <SegGroup testid="van-crew" value={g.crew} onChange={(v) => setGoals({ crew: v })}
              options={[{ value: "solo", label: "Solo" }, { value: "couple", label: "Couple" }, { value: "family", label: "Family" }]} />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/60 mb-1.5">Build path</p>
            <SegGroup testid="van-builder" value={g.builder} onChange={(v) => setGoals({ builder: v })}
              options={[{ value: "diy", label: "DIY" }, { value: "pro", label: "Pro Builder" }, { value: "both", label: "Both" }]} />
          </div>
        </div>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/60 mb-1.5">Priorities</p>
          <div className="flex flex-wrap gap-2">
            {GOAL_PRIORITIES.map((p) => (
              <Chip key={p} label={p} on={g.priorities.includes(p)} onClick={() => togglePriority(p)} testid={`van-pri-${p.replace(/\s/g, "-")}`} />
            ))}
          </div>
        </div>

        {/* Budget tier (mandatory) */}
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/60 mb-1.5">Budget tier <span className="text-[#DC2626]">*</span></p>
          <div className="space-y-2">
            {Object.values(BUDGET_TIERS).map((t) => {
              const on = g.budget_tier === t.key;
              return (
                <button key={t.key} onClick={() => setGoals({ budget_tier: t.key })} data-testid={`van-tier-${t.key}`}
                  className={`w-full text-left p-3 rounded-xl border-2 border-black ${on ? "bg-black text-[#FDFBF7] shadow-[4px_4px_0px_#000]" : "bg-white text-black"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-base">{t.label}</span>
                    <MonoBadge tone={on ? "gold" : "default"}>${(t.projectRange[0] / 1000)}k–${(t.projectRange[1] / 1000)}k</MonoBadge>
                  </div>
                  <p className={`font-mono text-[9px] mt-1 ${on ? "text-[#FDFBF7]/70" : "text-black/60"}`}>{t.blurb}</p>
                </button>
              );
            })}
          </div>
        </div>

        <NCard className="p-3 flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-black/60">Estimated target</p>
            <p className="font-serif text-2xl text-black">${lo.toLocaleString()}–${hi.toLocaleString()}</p>
          </div>
          <MonoBadge tone="gold">{g.priorities.length} priorities</MonoBadge>
        </NCard>

        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/60 mb-1.5">Anything else? (optional)</p>
          <textarea value={notes} maxLength={10000} onChange={(e) => setNotes(e.target.value)} onBlur={() => setGoals({ notes })}
            data-testid="van-notes" rows={4} placeholder="Tell us how you want to live in the van — as detailed as you like."
            className="w-full bg-white border-2 border-black rounded-xl px-3 py-2.5 font-mono text-xs text-black outline-none resize-none" />
          <p className="font-mono text-[8px] text-black/40 text-right mt-1">{notes.length}/10000</p>
        </div>
        <div className="h-2" />
      </div>
      <div className="shrink-0 border-t-2 border-black p-3 bg-[#FDFBF7]">
        <NBtn variant="gold" onClick={() => { setGoals({ notes }); setScreen("draft"); }} testid="van-goals-generate">
          <Sparkles size={15} /> Generate My Draft
        </NBtn>
      </div>
    </div>
  );
}
